import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../store';
import ApiError from '../types/error';
import { OperationStatus } from '../types';
import {
  CategoryType,
  Device,
  DeviceSubType,
  DeviceType,
  PostDeviceMappingRequestPayload
} from '../types/devices';

import {
  getDeviceMappingRequest,
  getDeviceMappingCleanup,
  postDeviceMappingFailure,
  postDeviceMappingRequest,
  postDeviceMappingSuccess,
  postDeviceMappingUpdate,
  postDeviceMappingSetRunning
} from '../features/devices/devices-slice';

import {
  isCustomError,
  isUUID,
  shouldClearIntervalForKey
} from '../utils/common';
import {
  CachedCategoryType,
  getCachedCategory,
  subscribeCachedCategoryChange
} from '../utils/cached-category';
import {
  getValidCachedRequestId,
  cleanupExpiredDeviceMappingCache
} from '../utils/cached-device-mapping';

import { DeviceTypeMap } from './useDeviceCategoryMapping';

export type DeviceTypeCombination = {
  key: string;
  deviceType: DeviceType;
  deviceSubType: DeviceSubType;
};

export type DeviceMappingRequestIdMapType = Record<
  string,
  {
    id: string;
    intervalId: NodeJS.Timeout | null;
  }
>;

/**
 * Extract deviceType/deviceSubType pairs from a map.
 */
export function extractDeviceTypeSubTypePairs(
  deviceTypeMap: DeviceTypeMap
): DeviceTypeCombination[] {
  const combinations: DeviceTypeCombination[] = [];
  Object.entries(deviceTypeMap ?? {}).forEach(([deviceType, subTypes]) => {
    subTypes.forEach((deviceSubType) => {
      combinations.push({
        key: `${deviceType}_${deviceSubType}`,
        deviceType: deviceType as DeviceType,
        deviceSubType
      });
    });
  });
  return combinations;
}

/**
 * Returns true if value or its first element is a custom error
 */
function isDeviceMappingErrored(value: ApiError | ApiError[]): boolean {
  if (Array.isArray(value)) {
    return isCustomError(value[0]);
  }
  return isCustomError(value);
}

/**
 * Main hook for device type mapping and polling.
 */
export function useDeviceTypeMapping(
  type: CategoryType,
  subTypeMap: DeviceTypeMap | undefined
): [boolean, Device[], OperationStatus, ApiError] {
  const [category, setCategory] = useState<CachedCategoryType[]>([]);
  const [isCached, setIsCached] = useState(false);
  const dispatch = useDispatch();

  const { user } = useSelector((state: RootState) => state.UserReducer);
  const userEmail = user?.email?.value;

  const { deviceMappingRequestError, deviceMappingRequestStatus, devices } =
    useSelector((state: RootState) => ({
      deviceMappingRequestError:
        state.DevicesReducer.deviceMappingRequest?.[type]?.error ?? null,
      deviceMappingRequestStatus:
        state.DevicesReducer.deviceMappingRequest?.[type]?.status ?? null,
      devices: state.DevicesReducer.devices
    }));

  // Track polling intervals and ids
  const deviceMappingRequestIdMap = useRef<DeviceMappingRequestIdMapType>({});

  // Listen for cached category changes with user email
  useEffect(() => {
    const updateCategories = () => setCategory(getCachedCategory(userEmail));
    const unsubscribe = subscribeCachedCategoryChange(updateCategories);
    updateCategories();
    return unsubscribe;
  }, [userEmail]);

  // Update isCached when category/type changes
  useEffect(() => {
    if (category.length === 0) return;
    setIsCached(category.some((cat) => cat.category === type));
  }, [category, type]);

  // Get all device type/subtype pairs
  const deviceTypeSubTypePairs = useMemo(() => {
    if (!subTypeMap || Object.keys(subTypeMap).length === 0) return [];
    return extractDeviceTypeSubTypePairs(subTypeMap);
  }, [subTypeMap]);

  // Combine all device mapping responses for this category
  const categorizedDevices: Device[] = useMemo(() => {
    if (
      !devices ||
      (deviceTypeSubTypePairs.length === 0 &&
        Object.keys(subTypeMap ?? {}).length === 0)
    )
      return [];
    const matchingValues =
      deviceTypeSubTypePairs.length > 0
        ? deviceTypeSubTypePairs
            .map((pair) => devices[pair.key])
            .filter((value) => value !== undefined)
        : Object.keys(subTypeMap ?? {})
            .map((element) => ({ key: element }))
            .map((pair) => devices[pair.key])
            .filter((value) => value !== undefined);
    if (matchingValues.length === 0) return [];
    const allResponsesAreArrays = matchingValues.every(Array.isArray);
    if (allResponsesAreArrays) {
      dispatch(postDeviceMappingSuccess({ type }));
      return matchingValues.flat();
    }
    const errorValue = matchingValues.find((value) =>
      isDeviceMappingErrored(value)
    );
    if (errorValue && isCustomError(errorValue)) {
      dispatch(
        postDeviceMappingFailure({
          type,
          error: errorValue
        })
      );
      return [];
    }
    return [];
  }, [devices, deviceTypeSubTypePairs, dispatch, type, subTypeMap]);

  // Post mapping requests when category is cached
  useEffect(() => {
    if (isCached && userEmail) {
      // Cleanup expired cache entries first
      cleanupExpiredDeviceMappingCache(userEmail);

      // Check if we have a valid cached request ID for this category
      const cachedRequestId = getValidCachedRequestId(type, userEmail);

      if (cachedRequestId) {
        // Set status to RUNNING to show loading indicator (no POST request)
        dispatch(postDeviceMappingSetRunning({ type }));

        // Use cached ID and directly update the devices state with the ID
        // This will trigger polling via the existing polling effect
        if (deviceTypeSubTypePairs.length > 0) {
          deviceTypeSubTypePairs.forEach((pair) => {
            dispatch(
              postDeviceMappingUpdate({
                id: cachedRequestId,
                device_type: pair.deviceType,
                device_sub_type: pair.deviceSubType
              })
            );
          });
        } else {
          Object.keys(subTypeMap ?? {}).forEach((key) => {
            dispatch(
              postDeviceMappingUpdate({
                id: cachedRequestId,
                device_type: key.split('_')[0] as DeviceType,
                device_sub_type: key.split('_')[1] as DeviceSubType
              })
            );
          });
        }
        return;
      }

      // No valid cache, make POST requests
      if (deviceTypeSubTypePairs.length > 0) {
        deviceTypeSubTypePairs.forEach((pair) => {
          const payload: PostDeviceMappingRequestPayload = {
            device_type: pair.deviceType,
            device_sub_type: pair.deviceSubType
          };
          dispatch(postDeviceMappingRequest({ type, payload }));
        });
      } else {
        Object.keys(subTypeMap ?? {}).forEach((key) => {
          const payload: PostDeviceMappingRequestPayload = {
            device_type: key.split('_')[0] as DeviceType,
            device_sub_type: key.split('_')[1] as DeviceSubType
          };
          dispatch(postDeviceMappingRequest({ type, payload }));
        });
      }
    }
  }, [deviceTypeSubTypePairs, dispatch, isCached, type, subTypeMap, userEmail]);

  // Polling effect
  useEffect(() => {
    if (
      !devices ||
      (deviceTypeSubTypePairs.length === 0 &&
        Object.keys(subTypeMap ?? {}).length === 0)
    )
      return;
    const intervalKeys: string[] = [];

    // Skip polling if already set, not a string, or not a UUID
    const shouldSkipPollingForKey = (key: string, value: unknown) =>
      !!deviceMappingRequestIdMap.current[key]?.intervalId ||
      typeof value !== 'string' ||
      !isUUID(value);

    // Clear polling interval if mapping is done or id changed
    const clearPollingIntervalForKey = (key: string) => {
      const deviceMappingValue = devices[key];
      if (
        shouldClearIntervalForKey(
          deviceMappingValue,
          deviceMappingValue as string,
          deviceMappingRequestIdMap.current[key]?.id
        )
      ) {
        const intervalId = deviceMappingRequestIdMap.current[key]?.intervalId;
        if (intervalId) {
          clearInterval(intervalId);
          delete deviceMappingRequestIdMap.current[key];
        }
      }
    };

    // Start polling for a key
    const setupPollingForKey = (key: string, deviceMappingValue: string) => {
      deviceMappingRequestIdMap.current[key] = {
        id: deviceMappingValue,
        intervalId: null
      };
      const payload = {
        id: deviceMappingValue,
        payload: {
          device_type: key.split('_')[0] as DeviceType,
          device_sub_type: key.split('_')[1] as DeviceSubType,
          category_type: type
        },
        userEmail
      };
      dispatch(getDeviceMappingRequest(payload));
      deviceMappingRequestIdMap.current[key].intervalId = setInterval(
        () => dispatch(getDeviceMappingRequest(payload)),
        10000
      );
    };

    // Handle polling for a single key
    const processPollingForKey = (key: string) => {
      const deviceMappingValue = devices[key];
      if (!deviceMappingValue) return;
      clearPollingIntervalForKey(key);
      if (shouldSkipPollingForKey(key, deviceMappingValue)) return;
      setupPollingForKey(key, deviceMappingValue as string);
      intervalKeys.push(key);
    };

    // Poll all device type/subtype pairs
    const processAllPolling = () => {
      deviceTypeSubTypePairs.length > 0
        ? deviceTypeSubTypePairs.forEach(({ key }) => {
            processPollingForKey(key);
          })
        : Object.keys(subTypeMap ?? {}).forEach((key) => {
            processPollingForKey(key);
          });
    };

    processAllPolling();

    // Cleanup polling intervals
    return () => {
      intervalKeys.forEach(clearPollingIntervalForKey);
    };
  }, [deviceTypeSubTypePairs, devices, dispatch, type, subTypeMap, userEmail]);

  // Cleanup effect for component unmount
  useEffect(() => {
    const currentIntervalMap = deviceMappingRequestIdMap.current;
    return () => {
      // Clear all polling intervals when component unmounts
      Object.keys(currentIntervalMap).forEach((key) => {
        const intervalId = currentIntervalMap[key]?.intervalId;
        if (intervalId) {
          clearInterval(intervalId);
        }
      });

      // Clear the device mapping request state
      dispatch(getDeviceMappingCleanup());
    };
  }, [dispatch]);

  // Return hook state
  return [
    isCached,
    categorizedDevices,
    deviceMappingRequestStatus,
    deviceMappingRequestError
  ];
}
